import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Logo from './Logo';
import LangToggle from './LangToggle';

// Used by /activate, /forgot, /reset. The landing has its own bespoke shell.
// This one is a centered card on the dark page with the brand mark at the top
// and a slim footer; works for short conversational forms.
interface AuthShellProps {
    children:    ReactNode;
    title:       string;
    eyebrow?:    string;
    subtitle?:   string;
    backTo?:     string;
    backLabel?:  string;
}

export default function AuthShell({
    children, title, eyebrow, subtitle, backTo, backLabel,
}: AuthShellProps) {
    const { t } = useTranslation();

    return (
        <div className="relative min-h-screen flex flex-col bg-slate-base">
            {/* Atmospheric mesh — fixed in the top-right */}
            <div
                aria-hidden="true"
                className="pointer-events-none fixed inset-0 bg-mesh-ember"
            />

            <header className="relative z-10 flex items-center justify-between px-6 sm:px-10 py-6">
                <Link to="/" className="block">
                    <Logo size="md" />
                </Link>
                <LangToggle />
            </header>

            <main className="relative z-10 flex-1 grid place-items-center px-6 sm:px-10 pb-16">
                <div className="w-full max-w-[440px]">
                    <div className="text-center mb-10 stagger">
                        {eyebrow && (
                            <div className="eyebrow mb-3">{eyebrow}</div>
                        )}
                        <h1 className="font-display text-4xl sm:text-5xl leading-[1.05] text-ink-on tracking-tight">
                            {title}
                        </h1>
                        {subtitle && (
                            <p className="mt-4 font-body text-ink-muted leading-relaxed text-balance">
                                {subtitle}
                            </p>
                        )}
                    </div>

                    <div className="card animate-fade-up">
                        {children}
                    </div>

                    {backTo && (
                        <div className="text-center mt-6">
                            <Link to={backTo} className="btn-link text-sm">
                                ← {backLabel ?? t('common.back')}
                            </Link>
                        </div>
                    )}
                </div>
            </main>

            <footer className="relative z-10 px-6 sm:px-10 py-6 text-center">
                <p className="text-[11px] uppercase tracking-widest2 text-ink-dim">
                    {t('brand.full')}
                </p>
            </footer>
        </div>
    );
}
